import React from "react";
import { useState, useEffect, useMemo } from "react";
import { Loader2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Combobox } from "@/components/ui/combobox";
import { useCachedFacebookPages } from "@/hooks/useCachedFacebookPages";
import { useCachedInstagramAccounts } from "@/hooks/useCachedInstagramAccounts";
import { useAuth } from "@/contexts/AuthContext";
import type { AdAccountSettings } from "@/hooks/useOnboardingSettings";

interface StepAdProfilesProps {
  adAccountId: string;
  settings: AdAccountSettings | null;
  onSave: (data: Partial<AdAccountSettings>) => Promise<any>;
  onValidChange: (valid: boolean) => void;
}

export function StepAdProfiles({
  adAccountId,
  settings,
  onSave,
  onValidChange,
}: StepAdProfilesProps) {
  const { user } = useAuth();
  const fbQuery = useCachedFacebookPages(user?.id, {
    refetchInterval: (query) => (query.state.data?.length ? false : 3000),
  });
  const igQuery = useCachedInstagramAccounts(user?.id, {
    refetchInterval: (query) => (query.state.data?.length ? false : 3000),
  });
  const fbPages = fbQuery.data ?? [];
  const fbLoading = fbQuery.isLoading;
  const igAccounts = igQuery.data ?? [];
  const igLoading = igQuery.isLoading;

  const [fbPageId, setFbPageId] = useState<string>(settings?.default_fb_page_id ?? "");
  const [igAccountId, setIgAccountId] = useState<string>(settings?.default_ig_account_id ?? "");
  const [hasSaved, setHasSaved] = useState(false);

  // Sync from loaded settings
  useEffect(() => {
    if (settings?.default_fb_page_id) setFbPageId(settings.default_fb_page_id);
    if (settings?.default_ig_account_id) setIgAccountId(settings.default_ig_account_id);
  }, [settings?.default_fb_page_id, settings?.default_ig_account_id]);

  // Show syncing state if data isn't ready yet
  const [mountTime] = useState(() => Date.now());
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (fbPages.length > 0) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [fbPages.length]);
  const isSyncing = fbPages.length === 0 && (fbLoading || now - mountTime < 30000);

  const fbOptions = useMemo(
    () => fbPages.map((p) => ({ value: p.page_id, label: p.page_name, subtitle: p.page_id })),
    [fbPages]
  );

  const igOptions = useMemo(
    () => igAccounts.map((a) => ({ value: a.instagram_id, label: `@${a.username}`, subtitle: a.instagram_id })),
    [igAccounts]
  );

  // Always valid — all fields optional
  useEffect(() => {
    onValidChange(true);
  }, [onValidChange]);

  // Auto-save when selections change (after initial load)
  useEffect(() => {
    if (!user?.id) return;
    // Debounce save
    const timeout = setTimeout(async () => {
      const fbPage = fbPages.find((p) => p.page_id === fbPageId);
      const igAccount = igAccounts.find((a) => a.instagram_id === igAccountId);
      await onSave({
        default_fb_page_id: fbPageId,
        default_fb_page_name: fbPage?.page_name ?? fbPageId,
        default_ig_account_id: igAccountId || null,
        default_ig_account_name: igAccount ? `@${igAccount.username}` : null,
        step_profiles_completed: true,
      });
      setHasSaved(true);
    }, 500);
    return () => clearTimeout(timeout);
  }, [fbPageId, igAccountId]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Ad Profiles</CardTitle>
        <CardDescription>
          Choose the default Facebook Page and Instagram account for your ads.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Facebook Page */}
        <div className="space-y-2">
          <Label>Facebook Page</Label>
          {isSyncing ? (
            <div className="flex items-center gap-2 rounded-md border border-border bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Syncing your pages...
            </div>
          ) : (
            <Combobox
              options={fbOptions}
              value={fbPageId}
              onValueChange={setFbPageId}
              placeholder="Select a Facebook Page..."
              searchPlaceholder="Search pages..."
              emptyMessage="No pages found yet. In some situations syncing can take a bit longer — please wait a moment or try refreshing the page."
            />
          )}
        </div>

        {/* Instagram Account */}
        <div className="space-y-2">
          <Label>Instagram Account</Label>
          {isSyncing ? (
            <div className="flex items-center gap-2 rounded-md border border-border bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Syncing your accounts...
            </div>
          ) : (
            <Combobox
              options={igOptions}
              value={igAccountId}
              onValueChange={setIgAccountId}
              placeholder="Select an Instagram account..."
              searchPlaceholder="Search accounts..."
              emptyMessage="No accounts found yet. In some situations syncing can take a bit longer — please wait a moment or try refreshing the page."
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
}
